import React from "react";
import { Link } from "react-router-dom";
import { Sparkle, ArrowRight } from "@phosphor-icons/react";
import InlineShareButton from "../components/InlineShareButton";
import { shareOrigin } from "../lib/shareOrigin";
import { CHANGELOG } from "../lib/changelog";

function fmt(d) {
  return new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function Entry({ e, latest }) {
  return (
    <li className="relative pl-8 pb-8 last:pb-0" data-testid={`changelog-entry-${e.date}-${e.tag.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <span className={`absolute left-0 top-1.5 w-3 h-3 rounded-full border-2 ${latest ? "bg-tac-primary border-tac-primary" : "bg-tac-bg border-tac-borderBright"}`} />
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <span className="font-mono-tac tabular text-[12px] uppercase tracking-wider text-tac-faint">{fmt(e.date)}</span>
        <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-tac-primary/50 text-tac-primary bg-tac-primary/10">{e.tag}</span>
        {latest && <span className="text-[10px] font-bold uppercase tracking-wider text-tac-safe">Latest</span>}
      </div>
      <h2 className="font-display font-black text-lg tracking-tight text-tac-text mb-2">{e.title}</h2>
      <ul className="space-y-1.5">
        {e.items.map((it) => (
          <li key={it} className="text-[15px] leading-relaxed text-tac-dim flex gap-2">
            <span className="text-tac-primary shrink-0">•</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </li>
  );
}

export default function WhatsNewPage() {
  const latest = CHANGELOG[0];
  const caption = [
    `✨ What's new in Firewatch — ${latest.title}`,
    latest.items[0],
    `Full update history: ${shareOrigin()}/whats-new`,
  ].join("\n");
  return (
    <div className="min-h-screen bg-tac-bg text-tac-text px-4 py-10" data-testid="whats-new-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="text-[12px] font-bold uppercase tracking-[0.2em] text-tac-primary inline-flex items-center gap-1.5">
            <Sparkle size={14} weight="fill" /> Season changelog
          </div>
          <InlineShareButton testid="whats-new-share-btn" url={`${shareOrigin()}/whats-new`} title="What's new in Firewatch" text={caption} caption={caption} />
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-3">What's New</h1>
        <p className="text-[15px] text-tac-dim leading-relaxed mb-10 max-w-xl">
          Every Firewatch update, newest first. Missed a few weeks? Catch up here, then open the
          Guide for how each feature works.
        </p>
        <ol className="border-l border-tac-border ml-1.5" data-testid="changelog-list">
          {CHANGELOG.map((e, i) => (
            <Entry key={`${e.date}-${e.title}`} e={e} latest={i === 0} />
          ))}
        </ol>
        <div className="flex flex-wrap gap-3 mt-10">
          <Link to="/guide" data-testid="whats-new-to-guide" className="inline-flex items-center gap-2 px-4 py-2.5 bg-tac-primary text-black font-bold uppercase tracking-wider text-[12px] rounded-sm hover:opacity-90 transition-opacity">
            Open the Guide <ArrowRight size={14} weight="bold" />
          </Link>
          <Link to="/about" data-testid="whats-new-to-about" className="inline-flex items-center gap-2 px-4 py-2.5 border border-tac-border text-tac-text font-bold uppercase tracking-wider text-[12px] rounded-sm hover:border-tac-borderBright transition-colors">
            Full feature list
          </Link>
        </div>
      </div>
    </div>
  );
}
